import { graphqlClient, publicGraphqlClient } from 'src/graphql/client'
import {
  AddCheckoutLinesDocument,
  AddCheckoutLinesMutationVariables,
  AddCheckoutPromoCodeDocument,
  AddCheckoutPromoCodeMutationVariables,
  AttachCustomerDocument,
  AttachCustomerMutationVariables,
  CampaignStoreSubcategoriesDocument,
  CampaignStoreSubcategoriesQueryVariables,
  CampaignStoreSubcategoryDocument,
  CampaignStoreSubcategoryQueryVariables,
  CheckoutDocument,
  CheckoutQueryVariables,
  CompleteCheckoutDocument,
  CompleteCheckoutMutationVariables,
  CreateCheckoutDocument,
  CreateCheckoutMutationVariables,
  CreateCheckoutPaymentDocument,
  CreateCheckoutPaymentMutationVariables,
  DeleteCheckoutLinesDocument,
  DeleteCheckoutLinesMutationVariables,
  GetSaleorCustomerDocument,
  InitializeTransactionDocument,
  InitializeTransactionMutationVariables,
  ProductVariantDocument,
  ProductVariantQueryVariables,
  ProductVariantsDocument,
  ProductVariantsQueryVariables,
  RemoveCheckoutPromoCodeDocument,
  RemoveCheckoutPromoCodeMutationVariables,
  SaleorOrderDocument,
  SaleorOrderQueryVariables,
  UpdateCheckoutBillingAddressDocument,
  UpdateCheckoutBillingAddressMutationVariables,
  UpdateCheckoutEmailDocument,
  UpdateCheckoutEmailMutationVariables,
  UpdateCheckoutLineQuantityDocument,
  UpdateCheckoutLineQuantityMutationVariables,
  UpdateSaleorCustomerDocument,
  UpdateSaleorCustomerMutationVariables,
} from 'src/saleor_graphql/generated/graphql'
import {
  GetAccountDocument,
  GetAccountCreatedContentsDocument,
  GetAffiliateBonusInfoDocument,
  GetAffiliateCountriesDocument,
  GetAlertsDocument,
  GetAlertsQueryVariables,
  GetBasicPostageCostsDocument,
  GetByDesignLoginLinkDocument,
  GetDoubleBonusReportsDocument,
  GetCampaignOrdersDocument,
  GetCampaignOrdersQueryVariables,
  GetCoachingBadgesDocument,
  GetCoachingBadgesQueryVariables,
  GetCoachingDashboardMarketingContentDocument,
  GetContactRequestByTokenDocument,
  GetContactRequestByTokenQueryVariables,
  GetContactRequestsDocument,
  GetDetailedCountriesDocument,
  GetDraftedCardsDocument,
  GetDraftedCardsQueryVariables,
  GetFlagsDocument,
  GetFontsDocument,
  GetImagesDocument,
  GetImagesQueryVariables,
  GetItemCategoriesDocument,
  GetItemDocument,
  GetItemQueryVariables,
  GetItemsDocument,
  GetItemsQueryVariables,
  GetLayoutDocument,
  GetLayoutQueryVariables,
  GetLayoutsDocument,
  GetLayoutsQueryVariables,
  GetFairUseMarketingContentDocument,
  GetCollectionDocument,
  GetFeaturedCardsDocument,
  GetLeaderboardMarketingContentDocument,
  GetLeaderBoardsDocument,
  GetMarketingBannersDocument,
  GetAffiliateUpsaleMarketingContentDocument,
  GetAffiliateUpsaleDialogMarketingContentDocument,
  GetMarketingContentDocument,
  GetMarketingParagraphByIdDocument,
  GetNotificationCountDocument,
  GetNotificationsDocument,
  GetNotificationsQueryVariables,
  GetOrderDocument,
  GetOrderQueryVariables,
  GetPaginatedInvitesDocument,
  GetPossibleSponsorsDocument,
  GetPaginatedInvitesQueryVariables,
  GetPaginatedRecipientsDocument,
  GetPaginatedRecipientsQueryVariables,
  GetPlanMoveDocument,
  GetPlanMoveQueryVariables,
  GetPlansDocument,
  GetPricingPageDocument,
  GetPromptingsCoachDiscountsDocument,
  GetPromptingsEventDatesDocument,
  GetReceivedPendingMembershipInvitesDocument,
  GetRemindersDocument,
  GetRemindersQueryVariables,
  GetSendableCardDocument,
  GetSendableCardQueryVariables,
  GetSentCardsCountDocument,
  GetSentCardsCountQueryVariables,
  GetSentCardsDocument,
  GetSentCardsQueryVariables,
  GetSharedCampaignsDocument,
  GetSharedCampaignsQueryVariables,
  GetSharedContactsDocument,
  GetSharedContactsQueryVariables,
  GetShouldVerifyEmailDocument,
  GetSponsorQueryVariables,
  GetStickerCategoriesDocument,
  GetStickerCategoriesQueryVariables,
  GetStickersDocument,
  GetStickersQueryVariables,
  GetUsersQueryVariables,
  GetDefaultSubscriptionIdsDocument,
  GetPaginatedContactRequestsQueryVariables,
  GetPaginatedContactRequestsDocument,
  GetGiftCategoriesDocument,
} from 'src/graphql/generated/graphql'
import * as graphql from 'src/graphql/generated/graphql'
import { LEGACY_SITE_URL } from 'src/graphql/client'
import { Store } from 'src/redux/store'
import { getPersistedSaleorAuth } from 'src/saleor/utils/auth'
import { saleorGraphQLClient } from 'src/saleor_graphql/client'
import { parseCheckout, withParsedAttributes } from '../utils'

export type SuccessfulOAuthResponse = {
  success: true
  access_token: string
  expires_in: number
  token_type: 'Bearer'
  refresh_token: string
  id_token: string
  error?: never
}
export type FailedOauthResponse = {
  success: false
  access_token?: never
  expires_in?: never
  token_type?: never
  id_token?: never
  error: string
}

export const getAccount = async () => {
  return (await graphqlClient.request(GetAccountDocument)).account
}

export const getAddPaymentSettingsDailyCap = async () => {
  return (
    await graphqlClient.request(graphql.GetAddPaymentSettingsDailyCapDocument)
  ).account
}

export const getAffiliateBonusInfo = async () =>
  await graphqlClient.request(GetAffiliateBonusInfoDocument)

export const getAffiliateCountries = async () => {
  return await publicGraphqlClient.request(GetAffiliateCountriesDocument)
}

export const getAlerts = async (variables: GetAlertsQueryVariables) =>
  await graphqlClient.request(GetAlertsDocument, variables)

export const getBasicPostageCosts = async () =>
  await graphqlClient.request(GetBasicPostageCostsDocument)

export const getDoubleBonusReports = async () =>
  (await graphqlClient.request(GetDoubleBonusReportsDocument))
    .doubleBonusReports

export const getAvailableDailyHeartfelt = async () =>
  (await graphqlClient.request(graphql.GetAvailableDailyHeartfeltDocument))
    .account

export const getAvailableUnlimitedDailyHeartfelt = async () =>
  (
    await graphqlClient.request(
      graphql.GetAvailableUnlimitedDailyHeartfeltDocument,
    )
  ).account

export const getByDesignLoginLink = async () => {
  return (await graphqlClient.request(GetByDesignLoginLinkDocument))
    .bydesignLoginLink
}

export const getShouldVerifyEmail = async () => {
  return (await graphqlClient.request(GetShouldVerifyEmailDocument))
    .shouldVerifyEmail
}
export const getUserApplicationSettings = async (
  variables: graphql.GetUserApplicationSettingsQueryVariables,
) =>
  (
    await graphqlClient.request(
      graphql.GetUserApplicationSettingsDocument,
      variables,
    )
  ).getUserApplicationSettings

export const getPlansDescriptions = async () => {
  return (await graphqlClient.request(graphql.GetPlansDescriptionsDocument))
    .plansDescriptions
}

export const getPlanAddonsDescriptions = async () => {
  return (
    await graphqlClient.request(graphql.GetPlanAddonsDescriptionsDocument)
  ).planAddonsDescriptions
}

export const getCard = async (variables: graphql.GetCardQueryVariables) =>
  await graphqlClient.request(graphql.GetCardDocument, variables)

export const getCardCatalog = async () => {
  // Use public client for non user specific catalog cards to be CDN cached
  const promises = [
    graphqlClient.request(graphql.GetCardCatalogDocument),
    publicGraphqlClient.request(graphql.GetPublicCardCatalogDocument),
  ] as const

  const [pub, priv] = await Promise.all(promises)

  return { ...pub, ...priv }
}

export const getCardCategories = async () =>
  (await graphqlClient.request(graphql.GetCardCategoriesDocument))
    .cardCategories

export const getCardCategory = async (
  variables: graphql.GetCardCategoryQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetCardCategoryDocument, variables))
    .cardCategory

export const getContact = async (variables: graphql.GetContactQueryVariables) =>
  (await graphqlClient.request(graphql.GetContactDocument, variables)).contact

export const getContacts = async (
  variables: graphql.GetContactsQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetContactsDocument, variables)).contacts

export const getContactIds = async (
  variables: graphql.GetContactIdsQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetContactIdsDocument, variables))
    .paginatedContacts.allContacts

export const getMinimalContacts = async (
  variables: graphql.GetMinimalContactsQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetMinimalContactsDocument, variables))
    .paginatedContacts.allContacts

export const getContactChildren = async (
  variables: graphql.GetContactChildrenQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetContactChildrenDocument, variables))
    .contact.children

export const getGroup = async (variables: graphql.GetGroupQueryVariables) =>
  (await graphqlClient.request(graphql.GetGroupDocument, variables)).group

export const getPaginatedGroups = async (
  variables: graphql.GetPaginatedGroupsQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetPaginatedGroupsDocument, variables))
    .paginatedGroups

export const getAllGroups = async () => {
  return (await graphqlClient.request(graphql.GetAllGroupsDocument)).groups
}

export const getCustomCards = async (
  variables: graphql.GetCustomCardsQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetCustomCardsDocument, variables)).cards

export const getDeploymentInfo = async () => {
  const response = await fetch('/deployment-info.json')
  if (!response.ok) {
    throw new Error('could not get deployment info...')
  }

  const json = await response.json()
  return {
    datetime: new Date(json.datetime),
  }
}

export const getDigitalCardByToken = async (
  variables: graphql.GetDigitalCardByTokenQueryVariables,
) =>
  (
    await graphqlClient.request(
      graphql.GetDigitalCardByTokenDocument,
      variables,
    )
  ).digitalCardByToken

export const getDigitalGiftCardProduct = async (
  variables: graphql.GetDigitalGiftCardProductQueryVariables,
) =>
  (
    await graphqlClient.request(
      graphql.GetDigitalGiftCardProductDocument,
      variables,
    )
  ).digitalGiftCardProduct

export const getDigitalCardOrder = async (
  variables: graphql.GetDigitalCardQueryVariables,
) => {
  return (
    await graphqlClient.request(graphql.GetDigitalCardDocument, variables)
  ).digitalCard
}

export const getDigitalCardsReceived = async (
  variables: graphql.GetDigitalCardsReceivedQueryVariables,
) => {
  return (
    await graphqlClient.request(
      graphql.GetDigitalCardsReceivedDocument,
      variables,
    )
  ).digitalCardsReceived
}

export const getDetailedCountries = async () => {
  return (await publicGraphqlClient.request(GetDetailedCountriesDocument))
    .detailedCountries
}

export const getPhoneNumberCountries = async () => {
  return (
    await publicGraphqlClient.request(graphql.GetPhoneNumberCountriesDocument)
  ).phoneNumberCountries
}

export const getLegacyPicturePlusCards = async (
  variables: graphql.GetLegacyPicturePlusCardsQueryVariables,
) =>
  (
    await graphqlClient.request(
      graphql.GetLegacyPicturePlusCardsDocument,
      variables,
    )
  ).legacyPicturePlusCards

export const getSendableCard = async (
  variables: GetSendableCardQueryVariables,
) => await graphqlClient.request(GetSendableCardDocument, variables)

export const getSendableCards = async (
  variables: graphql.GetSendableCardsQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetSendableCardsDocument, variables))
    .sendableCards

export const getGetDesignColorsBySendableCardID = async (
  variables: graphql.GetDesignColorsBySendableCardIdQueryVariables,
) =>
  (
    await graphqlClient.request(
      graphql.GetDesignColorsBySendableCardIdDocument,
      variables,
    )
  ).sendableCard.designColors

export const getSentCardsCount = async (
  variables: GetSentCardsCountQueryVariables,
) => await graphqlClient.request(GetSentCardsCountDocument, variables)

export const getDraftedCards = async (
  variables: GetDraftedCardsQueryVariables,
) => (await graphqlClient.request(GetDraftedCardsDocument, variables)).drafts

export const getItemCategories = async () =>
  (await graphqlClient.request(GetItemCategoriesDocument)).itemCategories

export const getItemCategory = async (
  variables: graphql.GetItemCategoryQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetItemCategoryDocument, variables))
    .itemCategory

export const getItem = async (variables: GetItemQueryVariables) =>
  await graphqlClient.request(GetItemDocument, variables)

export const getItems = async (variables: GetItemsQueryVariables) =>
  await graphqlClient.request(GetItemsDocument, variables)

export const getLeaderBoards = async () =>
  (await graphqlClient.request(GetLeaderBoardsDocument)).leaderBoards

export const getCampaign = async (
  variables: graphql.GetCampaignQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetCampaignDocument, variables)).campaign

export const getPaginatedCampaigns = async (
  variables: graphql.GetPaginatedCampaignsQueryVariables,
) =>
  (
    await graphqlClient.request(
      graphql.GetPaginatedCampaignsDocument,
      variables,
    )
  ).paginatedCampaigns

export const getUpsells = async () => {
  return (await graphqlClient.request(graphql.GetUpsellsDocument)).account
    .upsells
}

export const getSentCards = async (variables: GetSentCardsQueryVariables) =>
  (await graphqlClient.request(GetSentCardsDocument, variables))
    .paginatedSentCards

export const getCampaignOrders = async (
  variables: GetCampaignOrdersQueryVariables,
) =>
  (await graphqlClient.request(GetCampaignOrdersDocument, variables)).campaign
    .orders

export const getPaginatedInvites = async (
  variables: GetPaginatedInvitesQueryVariables,
) => {
  return (await graphqlClient.request(GetPaginatedInvitesDocument, variables))
    .paginatedInvites
}

export const getFlags = async () =>
  await graphqlClient.request(GetFlagsDocument)

export const getMarketingContent = async () =>
  // TODO: this query is too large to use public graphql client, split up further
  await graphqlClient.request(GetMarketingContentDocument)

export const getMarketingParagraphById = async (variables: { id: string }) =>
  await publicGraphqlClient.request(
    GetMarketingParagraphByIdDocument,
    variables,
  )

export const getAffiliateUpsaleMarketingContent = async () =>
  await publicGraphqlClient.request(GetAffiliateUpsaleMarketingContentDocument)

export const getAffiliateUpsaleDialogMarketingContent = async () =>
  await publicGraphqlClient.request(
    GetAffiliateUpsaleDialogMarketingContentDocument,
  )

export const getFairUseMarketingContent = async () =>
  await publicGraphqlClient.request(GetFairUseMarketingContentDocument)

export const getCollection = async (variables: { id: string }) =>
  await publicGraphqlClient.request(GetCollectionDocument, variables)

export const getFeaturedCards = async () =>
  await publicGraphqlClient.request(GetFeaturedCardsDocument)

export const getAffiliateJoinMarketingContent = async () =>
  await publicGraphqlClient.request(
    graphql.GetAffiliateJoinMarketingContentDocument,
  )

export const getLeaderBoardMarketingContent = async () =>
  await publicGraphqlClient.request(GetLeaderboardMarketingContentDocument)

export const getCoachingDashboardMarketingContent = async () =>
  await publicGraphqlClient.request(
    GetCoachingDashboardMarketingContentDocument,
  )

export const getMarketingBanners = async () =>
  (await publicGraphqlClient.request(GetMarketingBannersDocument)).banners

export const getActRecipientActionScreenContent = async () =>
  await graphqlClient.request(
    graphql.GetActRecipientActionScreenContentDocument,
  )

export const getGratitudeDayMarketingContent = async () =>
  await publicGraphqlClient.request(
    graphql.GetGratitudeDayMarketingContentDocument,
  )

export const getCreditCardManagerMarketingContent = async () =>
  await publicGraphqlClient.request(
    graphql.GetCreditCardManagerMarketingContentDocument,
  )

export const getContactRequesMarketingContent = async () =>
  await publicGraphqlClient.request(
    graphql.GetContactRequestMarketingContentDocument,
  )

export const getSubmitRequestedContactMarketingContent = async () =>
  await publicGraphqlClient.request(
    graphql.GetSubmitRequestedContactMarketingContentDocument,
  )

export const getProductionInfo = async (
  variables: graphql.GetProductionInfoQueryVariables,
) => await graphqlClient.request(graphql.GetProductionInfoDocument, variables)

export const getPaginatedRecipients = async (
  variables: GetPaginatedRecipientsQueryVariables,
) => await graphqlClient.request(GetPaginatedRecipientsDocument, variables)

export const getPaginatedContacts = async (
  variables: graphql.GetPaginatedContactsQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetPaginatedContactsDocument, variables))
    .paginatedContacts

export const getPaginatedMinimalContacts = async (
  variables: graphql.GetPaginatedMinimalContactsQueryVariables,
) =>
  (
    await graphqlClient.request(
      graphql.GetPaginatedMinimalContactsDocument,
      variables,
    )
  ).paginatedContacts

export const getPaginatedDraftedOrders = async (
  variables: graphql.GetDraftedOrdersQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetDraftedOrdersDocument, variables))
    .paginatedOrders

export const getLayouts = async (variables: GetLayoutsQueryVariables) =>
  await graphqlClient.request(GetLayoutsDocument, variables)

export const getLayout = async (variables: GetLayoutQueryVariables) =>
  await graphqlClient.request(GetLayoutDocument, variables)

export const getImages = async (variables: GetImagesQueryVariables) =>
  (await graphqlClient.request(GetImagesDocument, variables)).images

export const getGiftCategories = async () =>
  (await graphqlClient.request(GetGiftCategoriesDocument)).giftCategories

export const getGift = async (variables: graphql.GetGiftQueryVariables) =>
  (await graphqlClient.request(graphql.GetGiftDocument, variables)).gift

export const getGifts = async (variables: graphql.GetGiftsQueryVariables) =>
  (await graphqlClient.request(graphql.GetGiftsDocument, variables)).gifts

export const getCreditsBalance = async () =>
  (await graphqlClient.request(graphql.GetCreditsBalanceDocument)).account
    .credits

export const getDetailedReferralCredits = async () => {
  return (
    await graphqlClient.request(graphql.GetDetailedReferralCreditsDocument)
  ).account.detailedReferralCredits
}

export const getOrder = async (variables: GetOrderQueryVariables) =>
  await graphqlClient.request(GetOrderDocument, variables)

export const getFonts = async () =>
  await graphqlClient.request(GetFontsDocument)

export const getCoachingBadges = async (
  variables: GetCoachingBadgesQueryVariables,
) => {
  return await graphqlClient.request(GetCoachingBadgesDocument, variables)
}

export const getPlans = async () =>
  (await publicGraphqlClient.request(GetPlansDocument)).plans

export const getPlanMove = async (variables: GetPlanMoveQueryVariables) => {
  return await graphqlClient.request(GetPlanMoveDocument, variables)
}

export const getPricingPage = async () =>
  (await publicGraphqlClient.request(GetPricingPageDocument)).pricingPage

export const getNotifications = async (
  variables: GetNotificationsQueryVariables,
) => {
  return await graphqlClient.request(GetNotificationsDocument, variables)
}

export const getNotificationCount = async () =>
  (await graphqlClient.request(GetNotificationCountDocument)).notificationCount

export const getReceivedPendingMembershipInvites = async () => {
  return (
    await graphqlClient.request(GetReceivedPendingMembershipInvitesDocument)
  ).receivedPendingMembershipInvites
}

export const getReminders = async (variables: GetRemindersQueryVariables) => {
  return await graphqlClient.request(GetRemindersDocument, variables)
}

export const getDigitalCards = async (
  variables: graphql.GetDigitalCardsQueryVariables,
) => {
  return (
    await graphqlClient.request(graphql.GetDigitalCardsDocument, variables)
  ).digitalCards
}

export const getSharedCampaigns = async (
  variables: GetSharedCampaignsQueryVariables,
) => {
  return await graphqlClient.request(GetSharedCampaignsDocument, variables)
}

export const getSharedContacts = async (
  variables: GetSharedContactsQueryVariables,
) => {
  return await graphqlClient.request(GetSharedContactsDocument, variables)
}

export const getContactRequests = async () => {
  return await graphqlClient.request(GetContactRequestsDocument)
}

export const getContactRequestByToken = async (
  variables: GetContactRequestByTokenQueryVariables,
) => {
  return (
    await graphqlClient.request(GetContactRequestByTokenDocument, variables)
  ).contactRequestByToken
}

export const getPaginatedContactRequests = async (
  variables: GetPaginatedContactRequestsQueryVariables,
) => await graphqlClient.request(GetPaginatedContactRequestsDocument, variables)

export const getPromptingsCoachDiscounts = async () => {
  const response = await graphqlClient.request(
    GetPromptingsCoachDiscountsDocument,
  )
  return response.account.promptingsCoach?.discounts ?? []
}

export const getPromptingsEventDates = async () => {
  return await graphqlClient.request(GetPromptingsEventDatesDocument)
}

export const getAccounCreatedContents = async () =>
  await graphqlClient.request(GetAccountCreatedContentsDocument)

export const getSponsor = async (variables: GetSponsorQueryVariables) =>
  (await graphqlClient.request(graphql.GetSponsorDocument, variables)).sponsor

export const getPossibleSponsors = async () =>
  (await graphqlClient.request(GetPossibleSponsorsDocument)).account
    .possibleSponsors

export const getStickers = async (variables: GetStickersQueryVariables) =>
  (await graphqlClient.request(GetStickersDocument, variables)).stickers

export const getSubscriptions = async (
  variables: graphql.GetSubscriptionsQueryVariables,
) =>
  (await graphqlClient.request(graphql.GetSubscriptionsDocument, variables))
    .account.subscriptions

export const getUsers = async (variables: GetUsersQueryVariables) =>
  (await graphqlClient.request(graphql.GetUsersDocument, variables)).users

export const getUserCardTokens = async () =>
  (await graphqlClient.request(graphql.GetUserCardTokensDocument)).account

export const getExclusiveOffer = async () =>
  (await graphqlClient.request(graphql.GetExclusiveOfferDocument))
    .exclusiveOffer

export const authorizeSaleorOIDC = async ({
  codeChallenge,
  redirectUri,
}: {
  codeChallenge: string
  redirectUri: string
}): Promise<string> => {
  const token = Store.getState().user.controlData.token
  const headers = {
    Authorization: `Token ${token}`,
  }

  const url = `${LEGACY_SITE_URL}/soc_oauth2/authorize_frontend/?response_type=code&code_challenge=${codeChallenge}&code_challenge_method=S256&client_id=${
    process.env.REACT_APP_SALEOR_OIDC_CLIENT_ID
  }&redirect_uri=${encodeURIComponent(redirectUri)}`
  const promise = fetch(url, { headers })
  const response = await promise
  if (!response.ok) {
    throw new Error(
      `Failed to authorize: (${response.status}) ${response.body}`,
    )
  }

  const json = await response.json()
  return json.code
}

export const getSaleorAccessToken = async (
  authorizationCode: string,
  codeVerifier: string,
  redirectUri: string,
) => {
  const formData = new URLSearchParams()
  formData.append('client_id', `${process.env.REACT_APP_SALEOR_OIDC_CLIENT_ID}`)
  formData.append('code', authorizationCode)
  formData.append('code_verifier', codeVerifier)
  formData.append('redirect_uri', redirectUri)
  formData.append('grant_type', 'authorization_code')

  const url = `${LEGACY_SITE_URL}/oauth2/token/`

  try {
    const tokenResponse = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    })

    if (tokenResponse.status === 200) {
      const json = await tokenResponse.json()

      return {
        success: true,
        ...json,
      } as unknown as SuccessfulOAuthResponse
    }
    const json = await tokenResponse.json()

    return { success: false, ...json } as unknown as FailedOauthResponse
  } catch (err) {
    console.error(err)
    throw err
  }
}

export const getSaleorRefreshToken = async (refreshToken: string) => {
  const token = Store.getState().user.controlData.token
  const formData = new URLSearchParams()
  formData.append('client_id', `${process.env.REACT_APP_SALEOR_OIDC_CLIENT_ID}`)
  formData.append('refresh_token', refreshToken)
  formData.append('grant_type', 'refresh_token')

  const url = `${LEGACY_SITE_URL}/oauth2/token/`

  try {
    const tokenResponse = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Token ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData,
    })

    if (tokenResponse.status === 200) {
      const json = await tokenResponse.json()

      return {
        success: true,
        ...json,
      } as unknown as SuccessfulOAuthResponse
    }
    const json = await tokenResponse.json()

    return { success: false, ...json } as unknown as FailedOauthResponse
  } catch (err) {
    console.error(err)
    throw err
  }
}

export const getCheckout = async (variables: CheckoutQueryVariables) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    CheckoutDocument,
    variables,
    headers,
  )
  const { checkout } = res

  if (!checkout) return null

  return parseCheckout(checkout)
}

export const createCheckout = async (
  variables: CreateCheckoutMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    CreateCheckoutDocument,
    variables,
    headers,
  )
  const result = res.checkoutCreate

  if (result?.checkout) {
    return {
      ...result,
      checkout: parseCheckout(result.checkout),
    }
  }

  return result
}

export const updateCheckoutBillingAddress = async (
  variables: UpdateCheckoutBillingAddressMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    UpdateCheckoutBillingAddressDocument,
    variables,
    headers,
  )
  const result = res.checkoutBillingAddressUpdate

  if (result?.checkout) {
    return {
      ...result,
      checkout: parseCheckout(result.checkout),
    }
  }

  return result
}

export const addCheckoutLines = async (
  variables: AddCheckoutLinesMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    AddCheckoutLinesDocument,
    variables,
    headers,
  )

  const result = res.checkoutLinesAdd

  if (result?.checkout) {
    return {
      ...result,

      checkout: parseCheckout(result?.checkout),
    }
  }

  return result
}

export const addCheckoutPromoCode = async (
  variables: AddCheckoutPromoCodeMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    AddCheckoutPromoCodeDocument,
    variables,
    headers,
  )

  const result = res.checkoutAddPromoCode

  if (result?.checkout) {
    return {
      ...result,
      checkout: parseCheckout(result.checkout),
    }
  }

  return result
}

export const removeCheckoutPromoCode = async (
  variables: RemoveCheckoutPromoCodeMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    RemoveCheckoutPromoCodeDocument,
    variables,
    headers,
  )

  const result = res.checkoutRemovePromoCode
  if (result?.checkout) {
    return {
      ...result,
      checkout: parseCheckout(result.checkout),
    }
  }
  return result
}

export const getSaleorCustomer = async () => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    GetSaleorCustomerDocument,
    undefined,
    headers,
  )

  return res.me
}

export const updateSaleorCustomer = async (
  variables: UpdateSaleorCustomerMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    UpdateSaleorCustomerDocument,
    variables,
    headers,
  )
  return res.customerUpdate
}

export const deleteCheckoutLines = async (
  variables: DeleteCheckoutLinesMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    DeleteCheckoutLinesDocument,
    variables,
    headers,
  )
  const result = res.checkoutLinesDelete
  if (result?.checkout) {
    return {
      ...result,
      checkout: parseCheckout(result.checkout),
    }
  }
  return result
}

export const getProductVariant = async (
  variables: ProductVariantQueryVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    ProductVariantDocument,
    variables,
    headers,
  )

  if (!res?.productVariant) return null
  return withParsedAttributes(res.productVariant)
}

export const createTransaction = async (
  variables: InitializeTransactionMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    InitializeTransactionDocument,
    variables,
    headers,
  )

  return res.transactionInitialize
}

export const createCheckoutPayment = async (
  variables: CreateCheckoutPaymentMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    CreateCheckoutPaymentDocument,
    variables,
    headers,
  )
  return res.checkoutPaymentCreate
}

export const completeCheckout = async (
  variables: CompleteCheckoutMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    CompleteCheckoutDocument,
    variables,
    headers,
  )
  return res.checkoutComplete
}

export const getSaleorOrder = async (variables: SaleorOrderQueryVariables) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    SaleorOrderDocument,
    variables,
    headers,
  )
  return res.order
}

export const getProductVariants = async (
  variables: ProductVariantsQueryVariables,
) => {
  const res = await saleorGraphQLClient.request(
    ProductVariantsDocument,
    variables,
  )
  return {
    ...res.productVariants,
    edges:
      res.productVariants?.edges?.map(edge => ({
        ...edge,
        node: withParsedAttributes(edge.node),
      })) ?? [],
  }
}

export const updateCheckoutEmail = async (
  variables: UpdateCheckoutEmailMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    UpdateCheckoutEmailDocument,
    variables,
    headers,
  )
  const result = res.checkoutEmailUpdate
  if (result?.checkout) {
    return {
      ...result,
      checkout: parseCheckout(result.checkout),
    }
  }
  return result
}

export const attachCustomerToCheckout = async (
  variables: AttachCustomerMutationVariables,
  token?: string,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const attachedHeaders: { Authorization: string } | {} = token
    ? { Authorization: `Bearer ${token}` }
    : saleorAuth?.access_token
      ? { Authorization: `Bearer ${saleorAuth.access_token}` }
      : {}

  const res = await saleorGraphQLClient.request(
    AttachCustomerDocument,
    variables,
    attachedHeaders,
  )
  const result = res.checkoutCustomerAttach
  if (result?.checkout) {
    return {
      ...result,
      checkout: parseCheckout(result.checkout),
    }
  }
  return result
}

export const getCampaignStoreSubcategories = async (
  variables: CampaignStoreSubcategoriesQueryVariables,
) => {
  const res = await saleorGraphQLClient.request(
    CampaignStoreSubcategoriesDocument,
    variables,
  )
  return res.campaignStore
}

export const getCampaignStoreSubcategory = async (
  variables: CampaignStoreSubcategoryQueryVariables,
) => {
  const res = await saleorGraphQLClient.request(
    CampaignStoreSubcategoryDocument,
    variables,
  )

  return res.products
}

export const updateCheckoutLineQuantity = async (
  variables: UpdateCheckoutLineQuantityMutationVariables,
) => {
  const saleorAuth = getPersistedSaleorAuth()
  const headers: { Authorization: string } | {} = saleorAuth?.access_token
    ? { Authorization: `Bearer ${saleorAuth.access_token}` }
    : {}
  const res = await saleorGraphQLClient.request(
    UpdateCheckoutLineQuantityDocument,
    variables,
    headers,
  )
  const result = res.checkoutLinesUpdate
  if (result?.checkout) {
    return {
      ...result,
      checkout: parseCheckout(result.checkout),
    }
  }
  return result
}

export const getStickerCategories = async (
  variables: GetStickerCategoriesQueryVariables,
) =>
  (await graphqlClient.request(GetStickerCategoriesDocument, variables))
    .stickerCategories ?? {
    results: [],
    hasMore: false,
    totalCount: 0,
  }

export const getDefaultSubscriptionIds = async () => {
  const res = await graphqlClient.request(GetDefaultSubscriptionIdsDocument)
  return res.defaultSubscriptionIds.content.split(',')
}

export const getPaginatedDigitalGiftCardProducts = async (
  variables: graphql.GetPaginatedDigitalGiftCardProductsQueryVariables,
) => {
  const response = await graphqlClient.request(
    graphql.GetPaginatedDigitalGiftCardProductsDocument,
    variables,
  )
  return response.paginatedDigitalGiftCardProducts
}

export const getDigitalGiftCardsAvailableCountries = async () => {
  const res = await graphqlClient.request(
    graphql.GetDigitalGiftCardsAvailableCountriesDocument,
  )
  return res.digitalGiftCardsAvailableCountries
}

export const getGlobalSettings = async () => {
  const res = await graphqlClient.request(graphql.GetGlobalSettingsDocument)
  return res.globalSettings
}
